import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Loader2,
  ArrowRight,
  Play,
  Eye,
  EyeOff,
  LogIn,
  Users,
  Film,
  Sparkles,
  AtSign,
  RefreshCw,
} from "lucide-react";
import ayerisLogo from "@/assets/ayeris-logo.png";
import AgeGateModal, { isAgeLocked } from "@/components/AgeGateModal";
import AgeBlockedScreen from "@/components/AgeBlockedScreen";
import LanguageDropdown from "@/components/LanguageDropdown";
import TmdbAttribution from "@/components/TmdbAttribution";
import InstallAppBanner from "@/components/InstallAppBanner";
import NativeAppWaitlistModal from "@/components/NativeAppWaitlistModal";
import SocialLinks from "@/components/SocialLinks";

const MOVIE_WORDS = [
  "Matrix", "Jedi", "Hobbit", "Groot", "Gandalf", "Neo", "Ripley", "Maverick",
  "Rocky", "Indy", "Marty", "Doc", "Yoda", "Spock", "Kirk", "Thor",
  "Loki", "Stark", "Fury", "Morpheus", "Trinity", "Joker", "Batman",
  "Pixel", "Reel", "Flick", "Scene", "Popcorn", "Cinema", "Montage",
];

const generateUsername = () => {
  const word = MOVIE_WORDS[Math.floor(Math.random() * MOVIE_WORDS.length)];
  const num = Math.floor(100 + Math.random() * 900);
  return `${word}${num}`;
};

type View = "main" | "signin" | "age-gate" | "signup" | "verify" | "join-code" | "join-pick" | "join-signin" | "join-name";

/** Reusable password input with show/hide toggle */
const PasswordInput = ({
  value,
  onChange,
  placeholder = "••••••••",
  required = true,
  minLength = 6,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
}) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        required={required}
        minLength={minLength}
        className="w-full px-4 pr-11 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        tabIndex={-1}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const initialView = (location.state as any)?.view as View | undefined;
  const [view, setView] = useState<View>(initialView || "main");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState(() => generateUsername());
  const [rememberMe, setRememberMe] = useState(true);
  const [roomCode, setRoomCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [joinDisplayName, setJoinDisplayName] = useState(() => generateUsername());
  const [joinAsUser, setJoinAsUser] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [verifyEmail, setVerifyEmail] = useState("");
  const [ageBlocked, setAgeBlocked] = useState(() => isAgeLocked());
  const [showWaitlist, setShowWaitlist] = useState(false);

  // Already logged in — redirect to home
  useEffect(() => {
    if (!authLoading && user) {
      navigate("/home", { replace: true });
    }
  }, [user, authLoading, navigate]);

  // If initial view is "signup", route through age-gate first (unless already verified)
  useEffect(() => {
    if (initialView === "signup") {
      const alreadyVerified = localStorage.getItem("ayeris_birth_month");
      if (!alreadyVerified && !ageBlocked) {
        setView("age-gate");
      }
    }
  }, [initialView, ageBlocked]);

  if (authLoading) return null;

  if (ageBlocked) {
    return <AgeBlockedScreen />;
  }

  const validateUsername = (v: string) => {
    if (v.length < 3) return t("landing.usernameRequired");
    if (v.length > 24) return t("landing.usernameTooLong");
    if (!/^[a-zA-Z0-9_]+$/.test(v)) return t("landing.usernameInvalid");
    return "";
  };

  const handleNewClick = () => {
    if (isAgeLocked()) {
      setAgeBlocked(true);
      return;
    }
    const alreadyVerified = localStorage.getItem("ayeris_birth_month");
    if (alreadyVerified) {
      setView("signup");
    } else {
      setView("age-gate");
    }
  };

  const handleAgeGateComplete = () => {
    setView("signup");
  };

  const handleAgeGateBlocked = () => {
    setAgeBlocked(true);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (view === "join-signin") {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          const { data: profile } = await (supabase.from("profiles") as any)
            .select("username")
            .eq("id", authUser.id)
            .single();
          setJoinDisplayName(profile?.username || authUser.email?.split("@")[0] || generateUsername());
        }
        setJoinAsUser(true);
        setView("join-name");
      } else {
        navigate("/home");
      }
    } catch (err: any) {
      setError(err.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    const usernameErr = validateUsername(username.trim());
    if (usernameErr) { setError(usernameErr); return; }

    setLoading(true);
    try {
      const { data: existing } = await (supabase.from("profiles") as any)
        .select("id")
        .ilike("username", username.trim())
        .maybeSingle();
      if (existing) {
        setError(t("landing.usernameTaken"));
        setLoading(false);
        return;
      }

      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/home`,
          data: { preferred_username: username.trim() },
        },
      });
      if (signUpError) throw signUpError;

      setVerifyEmail(email);
      setOtpCode("");
      setError("");
      setView("verify");
    } catch (err: any) {
      setError(err.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email: verifyEmail,
        token: otpCode.trim(),
        type: "signup",
      });
      if (error) throw error;
      navigate("/home", { replace: true });
    } catch (err: any) {
      setError(err.message || t("misc.invalidOrExpiredCode"));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError("");
    setMessage("");
    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: verifyEmail,
      });
      if (error) throw error;
      setMessage(t("landing.newCodeSent"));
    } catch (err: any) {
      setError(err.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const resetView = () => {
    setView("main");
    setError("");
    setMessage("");
  };

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col items-center justify-center px-4 py-6 relative overflow-hidden">
      <Helmet>
        <title>Ayeris – Discover What to Watch</title>
        <meta name="description" content="Stop scrolling, start watching. Swipe through personalised movie and TV recommendations and find your next favourite with Ayeris." />
        <link rel="canonical" href="https://ayeris.tv/landing" />
      </Helmet>
      {/* Background ambience */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-20"
          style={{ background: "radial-gradient(ellipse, hsl(231 99% 62% / 0.4), transparent 70%)" }}
        />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[400px] h-[300px] rounded-full opacity-10"
          style={{ background: "radial-gradient(ellipse, hsl(260 90% 60% / 0.4), transparent 70%)" }}
        />
      </div>

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-sm space-y-8"
      >
        {/* Branding */}
        <div className="text-center space-y-4">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
          >
            <img src={ayerisLogo} alt="Ayeris" className="w-24 h-24 rounded-3xl shadow-iris mx-auto" />
          </motion.div>
          <h1 className="font-display text-4xl font-bold text-foreground tracking-tight">
            Ayeris
          </h1>
          <p className="font-display text-lg font-semibold text-foreground leading-snug">
            {t("landing.headline")}
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-[280px] mx-auto">
            {t("landing.subheadline")}
          </p>
        </div>

        <AnimatePresence mode="wait">
          {/* ─── Main Menu ─── */}
          {view === "main" && (
            <motion.div
              key="main"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-3"
            >
              <div className="flex justify-center py-3">
                <LanguageDropdown />
              </div>
              <button
                onClick={() => navigate("/demo")}
                className="w-full py-3.5 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-base shadow-iris active:scale-95 transition-transform flex items-center justify-center gap-2"
              >
                <Play className="w-4 h-4" /> {t("landing.showDemo")}
              </button>
              <button
                onClick={() => setView("signin")}
                className="w-full py-3.5 rounded-xl bg-secondary text-foreground font-display font-semibold text-base border border-border active:scale-95 transition-transform flex items-center justify-center gap-2"
              >
                {t("landing.signIn")}
              </button>
              <button
                onClick={handleNewClick}
                className="w-full py-3 rounded-xl bg-secondary/60 text-foreground font-medium text-sm border border-border/50 active:scale-95 transition-transform flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" /> {t("landing.imNew")}
              </button>
              <div className="flex items-center gap-3 py-1">
                <div className="flex-1 h-px bg-border" />
                <span className="text-[11px] text-muted-foreground uppercase tracking-widest">{t("common.or")}</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <button
                onClick={() => setView("join-code")}
                className="w-full py-3 rounded-xl bg-secondary/60 text-muted-foreground font-medium text-sm border border-border/50 active:scale-95 transition-transform flex items-center justify-center gap-2"
              >
                <Users className="w-4 h-4" /> {t("landing.joinFriend")}
              </button>
            </motion.div>
          )}

          {/* ─── Age Gate (before signup) ─── */}
          {view === "age-gate" && (
            <motion.div
              key="age-gate"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <AgeGateModal
                onComplete={handleAgeGateComplete}
                onBlocked={handleAgeGateBlocked}
              />
              <button onClick={resetView} className="w-full text-center text-xs text-muted-foreground mt-4 hover:text-foreground transition-colors">
                ← {t("common.back")}
              </button>
            </motion.div>
          )}

          {/* ─── Sign In ─── */}
          {view === "signin" && (
            <motion.div
              key="signin"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="bg-gradient-card border border-border rounded-2xl p-5 shadow-card space-y-4">
                <h2 className="font-display text-lg font-bold text-foreground text-center">{t("landing.welcomeBack")}</h2>

                <form onSubmit={handleSignIn} className="space-y-3">
                  <div>
                     <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">{t("landing.emailLabel")}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                     <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">{t("landing.passwordLabel")}</label>
                    <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="w-4 h-4 rounded border-border bg-secondary text-primary accent-primary"
                      />
                      <span className="text-xs text-muted-foreground">{t("landing.rememberMe")}</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => navigate("/auth", { state: { forgotPassword: true } })}
                      className="text-xs text-primary hover:underline"
                    >
                      {t("auth.forgotPassword", "Forgot password?")}
                    </button>
                  </div>

                  {error && (
                    <p className="text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("landing.signIn")} <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>

                <div className="text-center">
                  <button onClick={() => { handleNewClick(); setError(""); }} className="text-xs text-primary font-medium">
                    {t("landing.noAccount")}
                  </button>
                </div>
              </div>

              <button onClick={resetView} className="w-full text-center text-xs text-muted-foreground mt-4 hover:text-foreground transition-colors">
                ← {t("common.back")}
              </button>
            </motion.div>
          )}

          {/* ─── Sign Up ─── */}
          {view === "signup" && (
            <motion.div
              key="signup"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="bg-gradient-card border border-border rounded-2xl p-5 shadow-card space-y-4">
                <h2 className="font-display text-lg font-bold text-foreground text-center">{t("landing.createAccount")}</h2>
                <p className="text-xs text-muted-foreground text-center">{t("landing.joinSubtitle")}</p>

                <form onSubmit={handleSignUp} className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">{t("landing.usernameLabel")}</label>
                    <div className="relative">
                      <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        maxLength={24}
                        className="w-full pl-9 pr-12 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                        placeholder={t("auth.usernamePlaceholder")}
                      />
                      <button
                        type="button"
                        onClick={() => setUsername(generateUsername())}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-muted transition-colors"
                        title={t("landing.generateRandom")}
                      >
                        <RefreshCw className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                  </div>

                  <div>
                     <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">{t("landing.emailLabel")}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                     <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">{t("landing.passwordLabel")}</label>
                    <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} />
                  </div>

                  {error && (
                    <p className="text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("landing.createAccountBtn")} <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>

                <div className="text-center">
                  <button onClick={() => { setView("signin"); setError(""); setMessage(""); }} className="text-xs text-primary font-medium">
                    {t("landing.hasAccount")}
                  </button>
                </div>
              </div>

              <button onClick={resetView} className="w-full text-center text-xs text-muted-foreground mt-4 hover:text-foreground transition-colors">
                ← {t("common.back")}
              </button>
            </motion.div>
          )}

          {/* ─── Verify Email (OTP) ─── */}
          {view === "verify" && (
            <motion.div
              key="verify"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="bg-gradient-card border border-border rounded-2xl p-5 shadow-card space-y-4">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/15 mx-auto mb-2 flex items-center justify-center">
                    <AtSign className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="font-display text-lg font-bold text-foreground">{t("landing.checkEmail")}</h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("landing.verifyHint")}
                  </p>
                </div>

                <form onSubmit={handleVerifyOtp} className="space-y-3">
                  <div>
                     <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">{t("landing.emailLabel")}</label>
                    <input
                      type="email"
                      value={verifyEmail}
                      readOnly
                      className="w-full px-4 py-2.5 rounded-xl bg-secondary/60 border border-border text-muted-foreground text-sm cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">{t("landing.verificationCode")}</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\s/g, ""))}
                      placeholder={t("landing.enterCode")}
                      className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm text-center tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
                      autoFocus
                    />
                  </div>

                  {error && (
                    <p className="text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>
                  )}
                  {message && (
                    <p className="text-xs text-primary bg-primary/10 rounded-lg px-3 py-2">{message}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading || otpCode.trim().length < 4}
                    className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("landing.verifySignIn")} <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>

                <div className="text-center">
                  <button
                    onClick={handleResendCode}
                    disabled={loading}
                    className="text-xs text-primary font-medium disabled:opacity-50"
                  >
                    {t("landing.resendCode")}
                  </button>
                </div>
              </div>

              <button onClick={() => { setError(""); setMessage(""); setView("signup"); }} className="w-full text-center text-xs text-muted-foreground mt-4 hover:text-foreground transition-colors">
                ← {t("common.back")}
              </button>
            </motion.div>
          )}

          {/* ─── Join: Step 1 — Room Code ─── */}
          {view === "join-code" && (
            <motion.div
              key="join-code"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="bg-gradient-card border border-border rounded-2xl p-5 shadow-card space-y-4">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/15 mx-auto mb-2 flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="font-display text-lg font-bold text-foreground">{t("landing.joinFriendTitle")}</h2>
                  <p className="text-xs text-muted-foreground mt-1">{t("landing.enterRoomCode")}</p>
                </div>

                <input
                  type="text"
                  inputMode="text"
                  maxLength={6}
                  value={roomCode}
                  onChange={(e) => setRoomCode(e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 6))}
                  placeholder="ABC123"
                  className="w-full px-4 py-3 rounded-xl bg-secondary border border-border text-foreground text-center text-2xl font-display font-bold tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-primary/50"
                />

                <div className="space-y-2">
                  <button
                    onClick={() => { setJoinAsUser(false); setJoinDisplayName(generateUsername()); setView("join-name"); }}
                    disabled={roomCode.length !== 6}
                    className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {t("landing.joinGuest")}
                  </button>
                  <button
                    onClick={() => { setError(""); setView("join-signin"); }}
                    disabled={roomCode.length !== 6}
                    className="w-full py-3 rounded-xl bg-secondary text-foreground font-display font-semibold text-sm border border-border active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <LogIn className="w-4 h-4" /> {t("landing.joinExisting")}
                  </button>
                </div>
              </div>

              <button onClick={resetView} className="w-full text-center text-xs text-muted-foreground mt-4 hover:text-foreground transition-colors">
                ← {t("common.back")}
              </button>
            </motion.div>
          )}

          {/* ─── Join: Step 2a — Sign In (existing user) ─── */}
          {view === "join-signin" && (
            <motion.div
              key="join-signin"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="bg-gradient-card border border-border rounded-2xl p-5 shadow-card space-y-4">
                <h2 className="font-display text-lg font-bold text-foreground text-center">{t("landing.signInToJoin")}</h2>

                <form onSubmit={handleSignIn} className="space-y-3">
                  <div>
                    <label className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1 block">{t("landing.emailLabel")}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1 block">{t("landing.passwordLabel")}</label>
                    <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} />
                  </div>

                  <div className="text-right -mt-1">
                    <button
                      type="button"
                      onClick={() => navigate("/auth", { state: { forgotPassword: true } })}
                      className="text-xs text-primary hover:underline"
                    >
                      {t("auth.forgotPassword", "Forgot password?")}
                    </button>
                  </div>

                  {error && (
                    <p className="text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("landing.signIn")} <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>
              </div>

              <button onClick={() => { setError(""); setView("join-code"); }} className="w-full text-center text-xs text-muted-foreground mt-4 hover:text-foreground transition-colors">
                ← {t("common.back")}
              </button>
            </motion.div>
          )}

          {/* ─── Join: Step 2b/3 — Display Name ─── */}
          {view === "join-name" && (
            <motion.div
              key="join-name"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="bg-gradient-card border border-border rounded-2xl p-5 shadow-card space-y-4">
                <div className="text-center">
                  <h2 className="font-display text-lg font-bold text-foreground">{t("landing.yourDisplayName")}</h2>
                  <p className="text-[11px] text-muted-foreground mt-1">{t("landing.displayNameHint")}</p>
                </div>

                <div className="relative">
                  <input
                    type="text"
                    value={joinDisplayName}
                    onChange={(e) => setJoinDisplayName(e.target.value)}
                    placeholder={t("landing.enterYourName")}
                    maxLength={20}
                    className="w-full px-4 pr-12 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <button
                    type="button"
                    onClick={() => setJoinDisplayName(generateUsername())}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-muted transition-colors"
                    title={t("landing.generateRandom")}
                  >
                    <RefreshCw className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setView(joinAsUser ? "join-signin" : "join-code")}
                    className="px-4 py-3 rounded-xl bg-secondary text-muted-foreground text-sm font-medium active:scale-95 transition-transform"
                  >
                    {t("common.back")}
                  </button>
                  <button
                    onClick={() => {
                      if (!joinDisplayName.trim()) return;
                      navigate(`/discover?join=${roomCode}&name=${encodeURIComponent(joinDisplayName.trim())}`);
                    }}
                    disabled={!joinDisplayName.trim()}
                    className="flex-1 py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <ArrowRight className="w-4 h-4" /> {t("common.join")}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Install Banner */}
      <div className="relative z-10 mt-6 w-full max-w-sm">
        <InstallAppBanner onWaitlistClick={() => setShowWaitlist(true)} />
      </div>

      <NativeAppWaitlistModal open={showWaitlist} onClose={() => setShowWaitlist(false)} />

      {/* Divider */}
      <div className="relative z-10 mt-8 w-full max-w-sm">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-border" />
          <div className="flex-1 h-px bg-border" />
        </div>
      </div>

      {/* SEO content section — visible to crawlers, adds publisher value */}
      <div className="relative z-10 mt-12 w-full max-w-lg text-left space-y-8">
        <section>
          <h2 className="font-display text-xl font-bold text-foreground mb-3">{t("landing.seo.howTitle")}</h2>
          <p className="text-sm text-secondary-foreground leading-relaxed mb-3">{t("landing.seo.howP1")}</p>
          <p className="text-sm text-secondary-foreground leading-relaxed">{t("landing.seo.howP2")}</p>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-foreground mb-3">{t("landing.seo.groupTitle")}</h2>
          <p className="text-sm text-secondary-foreground leading-relaxed mb-3">{t("landing.seo.groupP1")}</p>
          <p className="text-sm text-secondary-foreground leading-relaxed">{t("landing.seo.groupP2")}</p>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-foreground mb-3">{t("landing.seo.whyTitle")}</h2>
          <ul className="space-y-2 text-sm text-secondary-foreground">
            <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✦</span><span>{t("landing.seo.why1")}</span></li>
            <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✦</span><span>{t("landing.seo.why2")}</span></li>
            <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✦</span><span>{t("landing.seo.why3")}</span></li>
            <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✦</span><span>{t("landing.seo.why4")}</span></li>
            <li className="flex items-start gap-2"><span className="text-primary mt-0.5">✦</span><span>{t("landing.seo.why5")}</span></li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-foreground mb-3">{t("landing.seo.privacyTitle")}</h2>
          <p className="text-sm text-secondary-foreground leading-relaxed">{t("landing.seo.privacyP1")}</p>
        </section>
      </div>

      {/* Footer */}
      <div className="relative z-10 mt-10 w-full max-w-sm">
        <SocialLinks className="mb-4" />
        <nav className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <a href="/about" className="text-[11px] text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2">{t("landing.about", "About")}</a>
          <span className="text-[11px] text-muted-foreground">·</span>
          <a href="/faq" className="text-[11px] text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2">{t("faq.title", "FAQ")}</a>
          <span className="text-[11px] text-muted-foreground">·</span>
          <a href="/guides" className="text-[11px] text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2">{t("guides.pageTitle", "Guides")}</a>
          <span className="text-[11px] text-muted-foreground">·</span>
          <a href="/blog" className="text-[11px] text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2">{t("blog.pageTitle", "Founder's Blog")}</a>
          <span className="text-[11px] text-muted-foreground">·</span>
          <a href="/privacy-policy" className="text-[11px] text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2">{t("settings.privacyPolicy")}</a>
          <span className="text-[11px] text-muted-foreground">·</span>
          <a href="/terms-of-service" className="text-[11px] text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2">{t("settings.termsOfService")}</a>
        </nav>
        <TmdbAttribution className="mt-4 mb-2" />
        <p className="text-[10px] text-muted-foreground/60 text-center mt-2 mb-4">© 2026 Ayeris.tv — All rights reserved.</p>
      </div>
    </div>
  );
};

export default Landing;
